import { useRef, useCallback, useEffect } from "react";

// Audio energy threshold — below this = silence
const SILENCE_THRESHOLD    = 0.015;
// Mouth movement threshold — below this = not moving
const STILL_THRESHOLD      = 4.0;   // mean absolute pixel diff 0-255
// How long audio must be loud + mouth still before flagging (ms)
const MISMATCH_DURATION    = 3000;
// Cooldown between alerts (ms)
const ALERT_COOLDOWN       = 20000;
// How many pixels wide/tall to sample from mouth region
const SAMPLE_SIZE          = 32;

export function useLipSyncDetection({ onViolation, enabled = true }) {
  const streamRef         = useRef(null);
  const analyserRef       = useRef(null);
  const audioCtxRef       = useRef(null);
  const sourceRef         = useRef(null);
  const prevPixelsRef     = useRef(null);
  const mismatchStartRef  = useRef(null);   // when mismatch began
  const lastAlertRef      = useRef(0);
  const runningRef        = useRef(false);

  // ── Audio setup ──────────────────────────────────────────────
  const connectAudio = useCallback(async (existingStream = null) => {
    try {
      let stream = existingStream;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl:  false,
          },
          video: false,
        });
        streamRef.current = stream;
      }
      const ctx      = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser();
      analyser.fftSize              = 512;
      analyser.smoothingTimeConstant = 0.6;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      // NOT connected to destination — silent analysis

      audioCtxRef.current  = ctx;
      analyserRef.current  = analyser;
      sourceRef.current    = source;
      runningRef.current   = true;
    } catch (e) {
      console.warn("LipSync: audio setup failed:", e);
    }
  }, []);

  // ── Audio energy (RMS) ───────────────────────────────────────
  const getAudioEnergy = useCallback(() => {
    if (!analyserRef.current) return 0;
    const data = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const norm = (data[i] - 128) / 128;
      sum += norm * norm;
    }
    return Math.sqrt(sum / data.length); // RMS 0-1
  }, []);

  // ── Mouth pixel sampling from canvas ────────────────────────
  const getMouthPixels = useCallback((canvasEl, detection) => {
    if (!canvasEl || !detection?.boundingBox) return null;
    const box = detection.boundingBox;

    const cw = canvasEl.width;
    const ch = canvasEl.height;
    if (!cw || !ch) return null;

    // Convert normalized center+size to pixel rect
    const xCenter   = box.xCenter ?? 0.5;
    const yCenter   = box.yCenter ?? 0.5;
    const faceWidth = box.width   ?? 0.2;
    const faceHeight= box.height  ?? faceWidth * 1.3;

    // Mouth is roughly in lower-third of face
    // Shift center down by 35% of face height from face center
    const mouthCenterY = yCenter + faceHeight * 0.35;
    const mouthCenterX = xCenter;
    const mouthW       = faceWidth  * 0.5;
    const mouthH       = faceHeight * 0.25;

    // Convert to pixel coords
    const px = Math.round((mouthCenterX - mouthW / 2) * cw);
    const py = Math.round((mouthCenterY - mouthH / 2) * ch);
    const pw = Math.round(mouthW * cw);
    const ph = Math.round(mouthH * ch);

    // Guard bounds
    const sx = Math.max(0, px);
    const sy = Math.max(0, py);
    const sw = Math.min(pw, cw - sx);
    const sh = Math.min(ph, ch - sy);
    if (sw <= 0 || sh <= 0) return null;

    try {
      const ctx = canvasEl.getContext("2d");
      const imageData = ctx.getImageData(sx, sy, sw, sh);
      // Downsample to SAMPLE_SIZE x SAMPLE_SIZE for speed
      const offscreen = document.createElement("canvas");
      offscreen.width  = SAMPLE_SIZE;
      offscreen.height = SAMPLE_SIZE;
      const offCtx = offscreen.getContext("2d");
      // Put current region then read back downsampled
      const tmp = document.createElement("canvas");
      tmp.width = sw; tmp.height = sh;
      tmp.getContext("2d").putImageData(imageData, 0, 0);
      offCtx.drawImage(tmp, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
      return offCtx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;
    } catch (e) {
      return null;
    }
  }, []);

  // ── Pixel diff between frames ────────────────────────────────
  const getMouthMovement = useCallback((currentPixels) => {
    if (!prevPixelsRef.current || !currentPixels) return 0;
    if (prevPixelsRef.current.length !== currentPixels.length) return 0;
    let totalDiff = 0;
    const len = currentPixels.length;
    // Only compare R channel (grayscale-ish, ignores color noise)
    for (let i = 0; i < len; i += 4) {
      totalDiff += Math.abs(currentPixels[i] - prevPixelsRef.current[i]);
    }
    return totalDiff / (len / 4); // mean absolute diff per pixel
  }, []);

  // ── Main analysis — called from ProctoringMonitor onResults ─
  const analyzeFrame = useCallback((canvasEl, detection) => {
    if (!enabled || !runningRef.current) return;

    const audioEnergy   = getAudioEnergy();
    const mouthPixels   = getMouthPixels(canvasEl, detection);
    const mouthMovement = getMouthMovement(mouthPixels);

    // Update prev pixels for next frame
    if (mouthPixels) prevPixelsRef.current = mouthPixels;

    const audioLoud   = audioEnergy   > SILENCE_THRESHOLD;
    const mouthStill  = mouthMovement < STILL_THRESHOLD;

    if (audioLoud && mouthStill) {
      // Audio detected but mouth not moving
      if (!mismatchStartRef.current) {
        mismatchStartRef.current = Date.now();
      } else if (
        Date.now() - mismatchStartRef.current > MISMATCH_DURATION &&
        Date.now() - lastAlertRef.current      > ALERT_COOLDOWN
      ) {
        lastAlertRef.current    = Date.now();
        mismatchStartRef.current = null;

        onViolation?.({
          type:     "lip_sync_mismatch",
          severity: "high",
          message:  "Audio detected but candidate mouth not moving",
          details: {
            audio_energy:    parseFloat(audioEnergy.toFixed(4)),
            mouth_movement:  parseFloat(mouthMovement.toFixed(4)),
            duration_ms:     MISMATCH_DURATION,
          },
        });
      }
    } else {
      // Reset timer when mouth moves or audio stops
      mismatchStartRef.current = null;
    }
  }, [enabled, getAudioEnergy, getMouthPixels, getMouthMovement, onViolation]);

  const start = useCallback(async (existingStream = null) => {
    if (runningRef.current) return;
    await connectAudio(existingStream);
  }, [connectAudio]);

  const stop = useCallback(() => {
    sourceRef.current?.disconnect();
    audioCtxRef.current?.close();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    audioCtxRef.current  = null;
    analyserRef.current  = null;
    sourceRef.current    = null;
    prevPixelsRef.current    = null;
    mismatchStartRef.current = null;
    lastAlertRef.current     = 0;
    runningRef.current       = false;
  }, []);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return { analyzeFrame, start, stop };
}
